import {h, Component} from 'preact'
import Dropdown from './index'
import {isInputEl} from '../../libs/typeGuards'
import Icon from '../assets'

export interface Item<T> {
  value: T,
  label: string
}

export interface ItemGroup<T> {
  items: Array<Item<T>>,
  label: string
}

interface Props<T> {
  optgroup: Array<ItemGroup<T>>,
  class?: string,
  placeholder?: string,
  selectedValue?: Item<T>['value'],
  onUpdate: (value: Item<T>['value']) => void
}

export class DropdownSelectizeGroup<T extends any> extends Component<Props<T>> {
  onSelect(item: Item<T>) {
    this.props.onUpdate(item.value)
  }

  getSelectedItem (): Item<T> | null {
    let selected: Item<T> | null = null
    this.props.optgroup.forEach(group => {
      group.items.forEach(item => {
        if (item.value !== this.props.selectedValue) return
        selected = item
      })
    })
    return selected
  }

  render() {
    const selectedLabel = this.getSelectedItem()?.label || this.props.placeholder
    const DropdownTrigger = (props: {onClick: (e: Event) => void}) => {
      return <div class='selectize-input' onClick={props.onClick}>
        <div class='item'>{selectedLabel}</div>
      </div>
    }
    return <div class={`selectize-control single ${this.props.class}`}>
      <Dropdown
        popupName='dropdown-selectize'
        closeOnDropdownClick
        triggerElement={DropdownTrigger}
        class={`selectize-dropdown single ${this.props.class} preact-selectize-dropdown`}>
        <div class='selectize-dropdown-content'>
          {this.props.optgroup.map(group => {
            return <div class='optgroup'>
              <div class='optgroup-header'>{group.label}</div>
              {group.items.map(item => {
                return <div class='option' data-selectable data-value={item.value} onClick={() => this.onSelect(item)}>{item.label}</div>
              })}
            </div>
          })}
        </div>
      </Dropdown>
    </div>
  }
}

type SingleOrMultiSelect = {
  single: true | undefined,
  onChange: (i: string) => void,
  selectedItem: string
} | {
  single: false,
  onChange: (i: Array<string>) => void,
  selectedItems: Array<string>
}

type InputProps = SingleOrMultiSelect & {
  items: Array<string>,
  placeholder: string,
  class?: string,
  allowEmptyValue?: false | boolean
}

interface InputState {
  searchText: string,
  inputInFocus: boolean
}

export class DropdownSelectizeInput extends Component<InputProps, InputState> {
  inputRef?: HTMLElement

  constructor() {
    super()
    this.state = {
      searchText: '',
      inputInFocus: false
    }
  }

  onSearchUpdated(newSearchText: string) {
    this.setState({
      searchText: newSearchText,
      inputInFocus: true
    })
  }

  selectItem(item: string) {
    if (!item && !this.props.allowEmptyValue) return
    if (!item && this.props.allowEmptyValue && item !== '') return
    if (this.props.single !== false) {
      this.props.onChange(item)
      return
    }
    if (this.props.selectedItems.indexOf(item) < 0) {
      this.props.onChange(this.props.selectedItems.concat(item))
    }
    this.setState({searchText: ''})
  }

  getItems() {
    const predicate = (item: string) => {
      if (this.props.single !== false) {
        return true
      }
      return !this.props.selectedItems.find(i => i === item)
    }

    return this.props.items
      .filter(predicate)
      .filter(item => item.toLowerCase().indexOf(this.state.searchText) >= 0)
  }

  componentWillReceiveProps(nextProps: InputProps) {
    if (nextProps.single === false) return
    if (this.state.searchText !== nextProps.selectedItem) {
      this.setState({searchText: nextProps.selectedItem})
    }
  }

  showNewBox() {
    if (this.props.single !== false) {
      return false
    }
    return this.props.items.length && this.state.searchText
  }

  remove(item: string) {
    if (this.props.single !== false) return
    this.props.onChange(this.props.selectedItems.filter(i => i !== item))
  }

  render() {
    const items = this.getItems()
    const showNewBox = this.showNewBox()
    const selectizeClass = this.props.single !== false ? 'single' : 'multi '

    return <div class={`selectize-control ${this.props.class} ${selectizeClass} plugin-remove_button preact-selectize-dropdown`}>
      <div class={`selectize-input items has-options ${this.props.single === false && this.props.selectedItems.length ? 'has-items' : ''} not-full`} onClick={() => {
          if (this.inputRef) this.inputRef.focus()
      }}>
        {this.props.single === false && this.props.selectedItems.map(item => {
          return <div class='item' data-value={item}>
            {item}
            <a href='#' class='remove' onClick={(e) => {
              e.preventDefault()
              this.remove(item)
            }} title='Remove'>
              <Icon icon='close' width={12} height={12} color={'currentColor'} />
            </a>
          </div>
        })}
        <input
          placeholder={!(this.props.single === false && this.props.selectedItems.length) ? this.props.placeholder : ''}
          type='text'
          onFocus={() => this.setState({inputInFocus: true})}
          onBlur={() => setTimeout(() => {
            this.setState({inputInFocus: false})
          }, 100)}
          onInput={e => {
            const target = e?.target
            if (!isInputEl(target)) return
            this.onSearchUpdated(target.value)
          }}
          onChange={() => {
            this.selectItem(this.state.searchText)
          }}
          onKeyDown={e => {
            if (e.keyCode === 9) {
              this.selectItem(this.state.searchText)
              e.preventDefault()
            }
          }}
          value={this.state.searchText}
          ref={r => this.inputRef = r}
        />
      </div>

      {
        this.state.inputInFocus && (items.length || showNewBox)
          ? <div class={`selectize-dropdown ${selectizeClass} ${this.props.class} preact-selectize-dropdown`}>
            <div class='selectize-dropdown-content'>
              {
                items.map(item => (
                  <div
                    class='option'
                    data-selectable
                    data-value={item}
                    onMouseDown={() => this.selectItem(item)}
                  >{item}</div>
                ))
              }
              {showNewBox
                ? <div data-selectable class='create' onMouseDown={() => this.selectItem(this.state.searchText)}>
                  Add <strong>{this.state.searchText} ...
                  </strong></div>
                : null}
            </div>
          </div>
          : null
      }
    </div>
  }
}
